'use client';

import { useState, useEffect } from 'react';
import SpeciesCard from './SpeciesCard';
import CategoryFilter from './CategoryFilter';
import SearchBar from './SearchBar';
import ConservationBanner from './ConservationBanner';
import SpeciesModal from './SpeciesModal';
import Icon from '@/components/ui/AppIcon';


interface DiveSite {
  name: string;
  probability: string;
}

interface SeasonalAvailability {
  season: string;
  probability: string;
}

interface Species {
  id: number;
  commonName: string;
  scientificName: string;
  category: string;
  image: string;
  alt: string;
  caption?: string;
  conservationStatus: string;
  sightingProbability: string;
  description: string;
  physicalDescription: string;
  behavior: string;
  size: string;
  habitat: string;
  diverNotes: string[];
  tags: string[];
  bestDiveSites: DiveSite[];
  seasonalAvailability: SeasonalAvailability[];
  interestingFacts: string[];
  conservationInfo: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  count: number;
}

const MarineLifeInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories: Category[] = [
  { id: 'all', name: 'All Species', icon: 'SparklesIcon', count: 30 },
  { id: 'fish', name: 'Fish', icon: 'FireIcon', count: 15 },
  { id: 'invertebrates', name: 'Invertebrates', icon: 'StarIcon', count: 8 },
  { id: 'sea-turtles', name: 'Sea Turtles', icon: 'ShieldCheckIcon', count: 3 },
  { id: 'marine-mammals', name: 'Marine Mammals', icon: 'HeartIcon', count: 2 },
  { id: 'rays', name: 'Rays & Sharks', icon: 'BoltIcon', count: 2 }];


  const speciesData: Species[] = [
  {
    id: 1,
    commonName: 'Green Sea Turtle',
    scientificName: 'Chelonia mydas',
    category: 'Sea Turtles',
    image: '/assets/images/cyprus-green-sea-turtle-underwater_scuba-1777384551987.jpg',
    alt: 'Green Sea Turtle (Chelonia mydas) swimming over seagrass in clear blue Cyprus waters',
    caption: 'One of the most iconic encounters in Cyprus, often seen gliding calmly over seagrass meadows.',
    conservationStatus: 'Endangered',
    sightingProbability: 'Very High',
    description: 'The Green Sea Turtle is one of the most iconic species encountered while diving in Cyprus. Frequently seen in shallow bays and seagrass habitats, it moves with a calm, unhurried grace that makes every encounter memorable. Cyprus is among the most important nesting sites for this species in the Mediterranean.',
    physicalDescription: 'Large sea turtle with a smooth, heart-shaped carapace ranging from olive to brown. Adults can weigh up to 200kg with a shell length of 1–1.2 metres. Named for the greenish colour of their fat, not their shell.',
    behavior: 'Herbivorous as adults, feeding primarily on seagrass and algae. Often seen resting on the seabed or swimming near the surface. Returns to Cyprus beaches for nesting during summer months.',
    size: '1–1.2 metres shell length, up to 200kg',
    habitat: 'Shallow coastal waters, seagrass beds, rocky reefs',
    diverNotes: [
    'Maintain a minimum distance of 3 metres — never chase or touch',
    'Frequently encountered at Zenobia Wreck and Green Bay',
    'Most active during summer nesting season (June–August)',
    'Avoid using bright torch beams directly at turtles'],

    tags: ['Reef', 'Beginner Friendly'],
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Very High' },
    { name: 'Green Bay', probability: 'High' },
    { name: 'Turtle Bay', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'Cyprus is one of the most important nesting sites for Green Sea Turtles in the Mediterranean',
    'They can hold their breath for up to 5 hours while resting',
    'Female turtles return to the same beach where they were born to lay their eggs',
    'They can live for over 80 years in the wild'],

    conservationInfo: 'Listed as Endangered on the IUCN Red List. Protected under Cyprus law and the EU Habitats Directive. Nesting beaches are monitored and protected. Divers should maintain a distance of at least 3 metres and never touch or chase turtles.'
  },
  {
    id: 6,
    commonName: 'Loggerhead Sea Turtle',
    scientificName: 'Caretta caretta',
    category: 'Sea Turtles',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_151f3d2d6-1777438025225.png",
    alt: 'Loggerhead sea turtle (Caretta caretta) with reddish-brown shell swimming near sandy seabed in Cyprus waters',
    caption: 'The Loggerhead is a regular visitor to Cyprus dive sites, recognised by its large head and powerful jaws.',
    conservationStatus: 'Vulnerable',
    sightingProbability: 'High',
    description: 'The Loggerhead Sea Turtle is a regular visitor to Cyprus waters and a significant nesting species on the island\'s beaches. Slightly smaller than the Green Turtle, it is distinguished by its large head and powerful jaws adapted for crushing hard-shelled prey. Encounters are most frequent during summer months.',
    physicalDescription: 'Reddish-brown carapace with a large, broad head. Adults typically reach 70–95cm shell length and weigh 80–200kg. Five pairs of costal scutes on the shell. Powerful jaws adapted for crushing prey.',
    behavior: 'Omnivorous, feeding on jellyfish, crustaceans, and molluscs. Often seen resting on the seabed or swimming in open water. Nests on Cyprus beaches from May to August. Can dive to depths over 200 metres.',
    size: '70–95cm shell length, 80–200kg',
    habitat: 'Coastal waters, open ocean, rocky reefs',
    diverNotes: [
    'Maintain a respectful distance — do not approach from above or below',
    'Most commonly encountered during summer months',
    'Frequently seen at Zenobia Wreck and Akrotiri Peninsula',
    'Never use flash photography close to turtles'],

    tags: ['Reef', 'Beginner Friendly'],
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'High' },
    { name: 'Amphitheatre', probability: 'Very High' },
    { name: 'Green Bay', probability: 'Moderate' },
    { name: 'Akrotiri Peninsula', probability: 'High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'They undertake some of the longest migrations of any sea turtle, travelling thousands of kilometres',
    'Their powerful jaws can crush hard-shelled prey such as conch and horseshoe crabs',
    'They can remain underwater for up to 7 hours when resting',
    'Nest temperature determines the sex of hatchlings — warmer nests produce more females'],

    conservationInfo: 'Listed as Vulnerable on the IUCN Red List. Protected under Cyprus law and international conventions. Major threats include fishing gear entanglement and coastal development. Divers should maintain distance and never interfere with nesting turtles.'
  },
  {
    id: 11,
    commonName: 'Leatherback Sea Turtle',
    scientificName: 'Dermochelys coriacea',
    category: 'Sea Turtles',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_12c34e06c-1772357911167.png',
    alt: 'Leatherback sea turtle (Dermochelys coriacea) with distinctive ridged leathery shell in open Mediterranean water',
    caption: 'The largest sea turtle in the world — an extremely rare and remarkable sighting in Cyprus waters.',
    conservationStatus: 'Vulnerable',
    sightingProbability: 'Extremely Rare',
    description: 'The Leatherback Sea Turtle is the largest living turtle and an extremely rare visitor to Cyprus waters. Unlike other sea turtles, it lacks a hard shell, instead having a flexible, leathery carapace. Occasional sightings in the Eastern Mediterranean make any encounter a truly exceptional event.',
    physicalDescription: 'The largest sea turtle, reaching up to 2 metres in length and weighing up to 900kg. Distinguished by its flexible, ridged leathery carapace rather than a hard shell. Dark grey to black with white spots.',
    behavior: 'Highly migratory, travelling vast distances across oceans. Feeds almost exclusively on jellyfish. Can dive to depths exceeding 1,000 metres. Maintains body temperature above ambient water temperature.',
    size: 'Up to 2 metres length, up to 900kg',
    habitat: 'Open ocean, occasionally coastal waters',
    diverNotes: [
    'Extremely rare in Cyprus — any sighting should be reported to marine authorities',
    'Observe from a distance and do not approach',
    'Most likely encountered in open water rather than at dive sites',
    'A once-in-a-lifetime encounter for most divers'],

    tags: ['Beginner Friendly'],
    bestDiveSites: [
    { name: 'Open water encounters', probability: 'Extremely Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Extremely Low' },
    { season: 'Summer', probability: 'Extremely Low' },
    { season: 'Autumn', probability: 'Extremely Low' },
    { season: 'Winter', probability: 'Extremely Low' }],

    interestingFacts: [
    'The largest living turtle, capable of weighing up to 900kg',
    'Can dive deeper than 1,000 metres — deeper than most other reptiles',
    'Feeds almost exclusively on jellyfish, consuming enormous quantities daily',
    'Maintains body temperature above the surrounding water through metabolic heat'],

    conservationInfo: 'Listed as Vulnerable on the IUCN Red List. Critically endangered in the Pacific. Threats include fishing gear entanglement, plastic ingestion (mistaken for jellyfish), and coastal development. Any sighting in Cyprus waters should be reported immediately to marine authorities.'
  },
  {
    id: 30,
    commonName: 'Dusky Grouper',
    scientificName: 'Epinephelus marginatus',
    category: 'Fish',
    image: '/assets/images/dusky-grouper-cyprus-epinephelus-marginatus-1777436562350.jpg',
    alt: 'Dusky Grouper (Epinephelus marginatus) close-up on a rocky reef in Cyprus Mediterranean waters',
    caption: 'A Dusky Grouper resting on a rocky reef in Cyprus — a classic Mediterranean dive encounter.',
    conservationStatus: 'Vulnerable',
    sightingProbability: 'Moderate',
    description: 'The Dusky Grouper is one of the most iconic reef predators in the Mediterranean. Known for its large size, expressive features, and slow, confident movement, it is often encountered resting near rocky reefs, caves, and wrecks. Though typically solitary, it can be surprisingly curious around divers.',
    physicalDescription: 'A large, robust fish with a broad head and wide mouth. Body coloration ranges from brownish-grey to olive with pale blotches and spots. Adults can reach up to 1.5 metres in length and weigh over 60kg.',
    behavior: 'Territorial and slow-moving. Typically solitary, resting near rocky outcrops, cave entrances, and wrecks. Often allows divers to approach closely. Juveniles are found in shallower water; adults prefer depths of 10–40m.',
    size: 'Up to 1.5 metres, over 60kg',
    habitat: 'Rocky reefs, caves, wrecks, 10–40 metres depth',
    diverNotes: [
    'Approach slowly and avoid sudden movements — they are curious but easily startled',
    'Never feed groupers, as this alters their natural behaviour',
    'Look for them resting in cave entrances and under rocky overhangs',
    'A sighting at Zenobia Wreck or Cape Greco is a highlight of any Cyprus dive'],

    tags: ['Reef', 'Wreck', 'Caves'],
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Moderate' },
    { name: 'Cape Greco', probability: 'Moderate' },
    { name: 'Jubilee Shoals', probability: 'Low' },
    { name: 'Green Bay', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Moderate' },
    { season: 'Summer', probability: 'Moderate' },
    { season: 'Autumn', probability: 'Moderate' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'All Dusky Groupers are born female and some later change sex to become male',
    'They can live for over 50 years, making large individuals particularly valuable to the ecosystem',
    'Once heavily overfished, they are now recovering in protected areas of the Mediterranean',
    'Their curious nature around divers makes them a favourite encounter on Cyprus reefs'],

    conservationInfo: 'Listed as Vulnerable on the IUCN Red List. Populations have declined significantly due to overfishing across the Mediterranean. Protected in marine reserves. Their slow growth and late sexual maturity make recovery difficult. Divers should observe respectfully and never feed or harass them.'
  },
  {
    id: 4,
    commonName: 'Mediterranean Moray Eel',
    scientificName: 'Muraena helena',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12acf8499-1772357910536.png",
    alt: 'Mediterranean Moray Eel (Muraena helena) with mottled brown and yellow pattern emerging from rocky crevice in Cyprus',
    caption: 'A Mediterranean Moray Eel peering from its rocky den — a common and fascinating sight on Cyprus reefs.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'High',
    description: 'The Mediterranean Moray Eel is a common and frequently misunderstood resident of Cyprus reefs. Often seen with its head protruding from rocky crevices, the constant opening and closing of its mouth is a breathing mechanism, not aggression. It is a fascinating species to observe from a respectful distance.',
    physicalDescription: 'Elongated, snake-like body with mottled brown and yellow coloration. Can reach up to 1.5 metres in length. No pectoral or pelvic fins. Constantly opens and closes mouth to pass water over gills.',
    behavior: 'Nocturnal predator, but often visible during day dives with head protruding from rocky crevices. Feeds on fish, octopus, and crustaceans. Territorial of its chosen den. Not aggressive unless provoked or cornered.',
    size: 'Up to 1.5 metres length',
    habitat: 'Rocky reefs, caves, crevices, 0–50 metres depth',
    diverNotes: [
    'Never put hands into rocky crevices — morays defend their dens',
    'The open-mouth posture is normal breathing behaviour, not aggression',
    'Commonly found at Zenobia Wreck and Cape Greco cave systems',
    'Observe from a distance and avoid blocking the entrance to their den'],

    tags: ['Reef', 'Wreck', 'Caves', 'Night Dive'],
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Very High' },
    { name: 'Chapel Reef', probability: 'High' },
    { name: 'Cape Greco', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'They have a second set of jaws in their throat called pharyngeal jaws',
    'Their skin secretes a protective mucus that can be mildly toxic',
    'They can tie themselves in knots to remove excess slime or escape predators',
    'Despite their appearance, bites to divers are rare and almost always provoked'],

    conservationInfo: 'Listed as Least Concern. Common throughout Cyprus waters. An important predator in the reef ecosystem. Divers should never put hands in crevices and should maintain a respectful distance. Never feed moray eels, as this can lead to conditioned and unpredictable behaviour.'
  },
  {
    id: 5,
    commonName: 'European Barracuda',
    scientificName: 'Sphyraena sphyraena',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_198908d2b-1777438020397.png",
    alt: 'European Barracuda (Sphyraena sphyraena) with elongated silver body swimming in open blue Mediterranean water near Cyprus',
    caption: 'A European Barracuda patrolling the reef edge — a sleek and impressive predator of Cyprus waters.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Moderate',
    description: 'The European Barracuda is a sleek, fast-moving predator regularly encountered at Cyprus dive sites. Often seen patrolling reef edges or hovering in open water, it is an impressive species to observe. Despite its intimidating appearance, it poses no threat to divers.',
    physicalDescription: 'Elongated, streamlined body with silver coloration and a darker back. Prominent lower jaw with sharp teeth. Can reach up to 1.6 metres in length. Large eyes and a forked tail for rapid acceleration.',
    behavior: 'Often solitary or in small groups. Curious about divers and may follow at a distance. Ambush predator that uses speed to catch prey. Sometimes forms schools in open water. More active during dawn and dusk.',
    size: 'Up to 1.6 metres length',
    habitat: 'Open water, reef edges, seagrass beds, 0–100 metres',
    diverNotes: [
    'Avoid wearing shiny jewellery — barracuda are attracted to reflective objects',
    'Maintain calm, slow movements if one approaches closely',
    'Often seen hovering near the surface or at reef edges',
    'Poses no threat to divers — observe calmly and enjoy the encounter'],

    tags: ['Reef', 'Wreck'],
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'High' },
    { name: 'Green Bay', probability: 'Moderate' },
    { name: 'Cape Greco', probability: 'Moderate' },
    { name: 'Jubilee Shoals', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Moderate' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'Can accelerate to speeds of up to 58 km/h in short bursts',
    'Their teeth are so sharp they can slice through fishing line',
    'Attracted to shiny objects, which they may mistake for prey fish',
    'Despite their fearsome appearance, attacks on humans are extremely rare'],

    conservationInfo: 'Listed as Least Concern. Populations are stable in Cyprus waters. An important apex predator in the marine ecosystem. Divers should avoid wearing shiny jewellery and maintain calm movements around barracuda.'
  },
  {
    id: 7,
    commonName: 'Flying Gurnard',
    scientificName: 'Dactylopterus volitans',
    category: 'Fish',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_17ff16974-1770411350011.png',
    alt: 'Flying Gurnard (Dactylopterus volitans) with extended wing-like pectoral fins displaying blue and orange patterns on sandy seabed in Cyprus',
    caption: 'A Flying Gurnard displaying its spectacular wing-like fins — one of the most unusual sightings in Cyprus waters.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Rare',
    description: 'The Flying Gurnard is one of the most unusual and visually striking fish in the Mediterranean. When threatened or excited, it spreads its enormous pectoral fins to reveal a spectacular display of blue spots and patterns. A rare sighting that is considered a highlight by many Cyprus divers.',
    physicalDescription: 'Distinctive fish with enormous pectoral fins that spread like wings, displaying vivid blue spots and patterns. Body is reddish-brown with bony plates. Can reach up to 50cm in length.',
    behavior: 'Bottom-dwelling fish that "walks" on modified pelvic fins. Uses wing-like pectorals for display and to startle predators. Feeds on small crustaceans and molluscs. Despite the name, it cannot fly but can glide short distances.',
    size: 'Up to 50cm length',
    habitat: 'Sandy and muddy bottoms, 20–100 metres depth',
    diverNotes: [
    'Move slowly and avoid sudden movements to prevent it from fleeing',
    'Best observed on sandy bottoms at sites like Cape Greco and Limassol reefs',
    'The wing display is triggered by perceived threat — maintain distance',
    'A rare sighting — worth taking time to observe and photograph carefully'],

    tags: ['Sandy Bottom', 'Macro'],
    bestDiveSites: [
    { name: 'Limassol Reefs', probability: 'Low' },
    { name: 'Cape Greco', probability: 'Low' },
    { name: 'Akrotiri Peninsula', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Low' },
    { season: 'Summer', probability: 'Moderate' },
    { season: 'Autumn', probability: 'Moderate' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'Their "wings" can span up to 60cm when fully extended',
    'They produce sounds by vibrating their swim bladder',
    'Despite their appearance, they are not related to flying fish',
    'Considered a lucky sighting by many Mediterranean divers'],

    conservationInfo: 'Listed as Least Concern. Rare in Cyprus waters but populations appear stable. Not targeted by fisheries. Divers lucky enough to encounter one should observe quietly to avoid disturbing them.'
  },
  {
    id: 8,
    commonName: 'Painted Comber',
    scientificName: 'Serranus scriba',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d2b82bfd-1772284536329.png",
    alt: 'Painted Comber (Serranus scriba) with distinctive orange and blue striped pattern near rocky reef in Cyprus',
    caption: 'The Painted Comber is one of the most colourful and reliably encountered fish on Cyprus reefs.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Very High',
    description: 'The Painted Comber is one of the most colourful and commonly encountered fish in Cyprus waters. Easily recognised by its distinctive orange-red vertical stripes and intricate blue markings, it is a reliable and photogenic species found on virtually every reef dive in Cyprus.',
    physicalDescription: 'Small, colourful fish with orange-red vertical stripes and blue markings that resemble Arabic script (hence the Latin name "scriba"). Reaches about 25cm in length. Distinctive blue line pattern on the head.',
    behavior: 'Territorial and curious, often approaches divers. Hermaphroditic — each individual has both male and female reproductive organs. Feeds on small fish and crustaceans. Often seen hovering near rocky outcrops.',
    size: 'Up to 25cm length',
    habitat: 'Rocky reefs, seagrass beds, 5–150 metres depth',
    diverNotes: [
    'One of the most reliably encountered fish on any Cyprus reef dive',
    'Often approaches divers out of curiosity — a good subject for underwater photography',
    'Look for them hovering near rocky outcrops and cave entrances',
    'Active throughout the year, including winter months'],

    tags: ['Reef', 'Macro', 'Beginner Friendly'],
    bestDiveSites: [
    { name: 'Green Bay', probability: 'Very High' },
    { name: 'Chapel Reef', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'Very High' },
    { name: 'Zenobia Wreck', probability: 'High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'High' }],

    interestingFacts: [
    'The blue markings on their head are unique to each individual, similar to fingerprints',
    'They are simultaneous hermaphrodites — one of the few fish species with this trait',
    'Can change colour intensity based on mood and environment',
    'A popular subject for underwater photographers due to their vivid colours'],

    conservationInfo: 'Listed as Least Concern. Very common throughout Cyprus waters. An important part of the reef ecosystem. No special conservation measures required, but divers should not harass individuals for photography.'
  },
  {
    id: 9,
    commonName: 'Chromis Damselfish',
    scientificName: 'Chromis chromis',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b794a602-1772357911377.png",
    alt: 'School of Chromis damselfish (Chromis chromis) swimming above rocky Mediterranean reef in Cyprus',
    caption: 'Dense schools of Chromis damselfish are a defining feature of Cyprus reef dives.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Very High',
    description: 'The Chromis Damselfish is one of the most abundant and visible fish on Cyprus reefs. Forming dense schools above rocky outcrops, these small, dark fish create dynamic underwater scenes and are a constant presence on virtually every dive in Cyprus.',
    physicalDescription: 'Small, dark brown to black fish reaching about 15cm. Juveniles are bright electric blue, gradually darkening with age. Deeply forked tail. Forms dense schools that move in synchronised patterns.',
    behavior: 'Highly social, forming large aggregations above rocky reefs. Feeds on zooplankton in the water column. Territorial during breeding season. Males guard eggs laid in rocky crevices.',
    size: 'Up to 15cm length',
    habitat: 'Rocky reefs, coastal waters, 0–40 metres depth',
    diverNotes: [
    'Schools of hundreds are a common sight on any Cyprus reef dive',
    'Look for bright blue juveniles near rocky crevices — a striking contrast to the adults',
    'Males become territorial during breeding season and may display aggressive behaviour',
    'An excellent indicator of a healthy, productive reef ecosystem'],

    tags: ['Reef', 'Beginner Friendly'],
    bestDiveSites: [
    { name: 'Green Bay', probability: 'Very High' },
    { name: 'Chapel Reef', probability: 'Very High' },
    { name: 'Zenobia Wreck', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'Very High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'High' }],

    interestingFacts: [
    'Schools can contain hundreds or even thousands of individuals',
    'Juveniles are electric blue, making them popular in the aquarium trade',
    'They produce sounds during courtship and territorial disputes',
    'Males become highly aggressive when guarding eggs'],

    conservationInfo: 'Listed as Least Concern. Extremely abundant throughout the Mediterranean. An important prey species for larger fish. Their abundance is a good indicator of healthy reef ecosystems.'
  },
  {
    id: 10,
    commonName: 'Salema Porgy',
    scientificName: 'Sarpa salpa',
    category: 'Fish',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1659e2425-1772284534056.png',
    alt: 'School of Salema Porgy (Sarpa salpa) with distinctive golden horizontal stripes swimming in clear Cyprus waters',
    caption: 'A school of Salema Porgy moving through the water column — a common and elegant sight on Cyprus reefs.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Very High',
    description: 'The Salema Porgy is a common schooling fish with distinctive golden horizontal stripes, frequently encountered grazing on seagrass and algae in Cyprus waters. Schools of hundreds of individuals are a regular sight on shallow reef dives throughout the island.',
    physicalDescription: 'Silvery fish with 10–12 distinctive golden horizontal stripes along the body. Can reach up to 45cm in length. Streamlined body with a slightly forked tail. Forms large, organised schools.',
    behavior: 'Herbivorous, feeding primarily on seagrass and algae. Forms large schools that move together in coordinated patterns. Most active during daylight hours. An important grazer in seagrass ecosystems.',
    size: 'Up to 45cm length',
    habitat: 'Seagrass beds, rocky reefs, coastal waters, 0–70 metres',
    diverNotes: [
    'Schools of hundreds are a common sight on shallow reef dives',
    'Often seen grazing on seagrass beds and algae-covered rocks',
    'Approachable and calm — good for beginner divers and photographers',
    'Frequently encountered at Green Bay, Zenobia, and Amphora Reef'],

    tags: ['Reef', 'Sandy Bottom', 'Beginner Friendly'],
    bestDiveSites: [
    { name: 'Green Bay', probability: 'Very High' },
    { name: 'Zenobia Wreck', probability: 'High' },
    { name: 'Amphora Reef', probability: 'Very High' },
    { name: 'Turtle Bay', probability: 'Very High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'High' }],

    interestingFacts: [
    'One of the few herbivorous fish in the Mediterranean',
    'In ancient times, consumption was reported to cause hallucinations due to algae in their diet',
    'Schools can contain hundreds of individuals',
    'They play a crucial role in controlling algae growth on reefs'],

    conservationInfo: 'Listed as Least Concern. Very abundant throughout Cyprus waters. An important herbivore in the ecosystem. Commercially fished but populations remain stable. Their presence indicates healthy seagrass beds.'
  },
  {
    id: 12,
    commonName: 'Rainbow Wrasse',
    scientificName: 'Coris julis',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee22eb19-1777438020222.png",
    alt: 'Rainbow Wrasse (Coris julis) male with vivid orange and blue coloration swimming near rocky reef in Cyprus',
    caption: 'The male Rainbow Wrasse is one of the most vividly coloured fish encountered on Cyprus reef dives.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Very High',
    description: 'The Rainbow Wrasse is one of the most colourful and active fish in Cyprus waters. Males and females display dramatically different colouration, with males showing vivid green-blue and orange tones. A constant presence on rocky reefs, it is one of the most reliably encountered species in Cyprus.',
    physicalDescription: 'Highly sexually dimorphic. Males are bright green-blue with an orange stripe along the body. Females are brown with white stripes. Can reach up to 25cm. Elongated body with a continuous dorsal fin.',
    behavior: 'Very active and constantly on the move. Feeds on small invertebrates, crustaceans, and molluscs. Males are territorial and perform elaborate courtship displays. Buries itself in sand at night for protection.',
    size: 'Up to 25cm length',
    habitat: 'Rocky reefs, seagrass beds, sandy areas, 0–120 metres',
    diverNotes: [
    'One of the most reliably encountered and colourful fish on Cyprus reefs',
    'Males are significantly more vivid than females — look for the orange lateral stripe',
    'Active and fast-moving — patience is needed for close-up photography',
    'Present year-round on virtually every reef dive in Cyprus'],

    tags: ['Reef', 'Macro', 'Beginner Friendly'],
    bestDiveSites: [
    { name: 'Green Bay', probability: 'Very High' },
    { name: 'Chapel Reef', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'Very High' },
    { name: 'Zenobia Wreck', probability: 'High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'High' }],

    interestingFacts: [
    'All individuals are born female and some change to male later in life (protogynous hermaphrodite)',
    'Males establish territories and harems of females',
    'Can change colour rapidly when excited or threatened',
    'One of the most photographed fish in the Mediterranean due to its vivid colours'],

    conservationInfo: 'Listed as Least Concern. Extremely abundant throughout Cyprus waters. An important part of the reef ecosystem. No conservation concerns. Their active behaviour makes them entertaining to observe.'
  },
  {
    id: 13,
    commonName: 'Peacock Wrasse',
    scientificName: 'Symphodus tinca',
    category: 'Fish',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_123c6c2b4-1772284535829.png',
    alt: 'Peacock Wrasse (Symphodus tinca) male with intricate blue and green patterns swimming near rocky substrate in Cyprus',
    caption: 'The Peacock Wrasse is a beautifully patterned resident of Cyprus rocky reefs.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'High',
    description: 'The Peacock Wrasse is a beautifully patterned fish with intricate blue, green, and orange markings. Commonly seen around Cyprus rocky reefs, males are particularly striking during breeding season when their colours intensify and they actively defend nesting territories.',
    physicalDescription: 'Colourful fish with complex patterns of blue, green, and orange. Males have distinctive blue markings on the head and a dark spot near the tail. Can reach up to 30cm. Females are less colourful with brown tones.',
    behavior: 'Territorial, especially during breeding season. Males build nests from algae and defend them actively. Feeds on small invertebrates and crustaceans. Active during the day, hides in crevices at night.',
    size: 'Up to 30cm length',
    habitat: 'Rocky reefs, seagrass beds, 0–50 metres depth',
    diverNotes: [
    'Males are most colourful and active during spring breeding season',
    'Look for nest-building behaviour on rocky substrates in shallow water',
    'Commonly encountered at Green Bay, Chapel Reef, and Amphora Reef',
    'Less common than Rainbow Wrasse but equally rewarding to observe'],

    tags: ['Reef', 'Macro'],
    bestDiveSites: [
    { name: 'Green Bay', probability: 'High' },
    { name: 'Chapel Reef', probability: 'High' },
    { name: 'Amphora Reef', probability: 'High' },
    { name: 'Cape Greco', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'Males build elaborate nests to attract females',
    'One of the few fish species that provides parental care',
    'Males guard the eggs until they hatch',
    'Colours become more vivid during breeding season'],

    conservationInfo: 'Listed as Least Concern. Common throughout Cyprus waters. An important part of the reef ecosystem. No conservation concerns. Interesting to observe during breeding season when males are most colourful and active.'
  },
  {
    id: 14,
    commonName: 'Mediterranean Parrotfish',
    scientificName: 'Sparisoma cretense',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cd1ad475-1772284535678.png",
    alt: 'Mediterranean Parrotfish (Sparisoma cretense) with distinctive beak-like mouth grazing on rocky reef in Cyprus',
    caption: 'The Mediterranean Parrotfish is a rare but ecologically important species on Cyprus reefs.',
    conservationStatus: 'Vulnerable',
    sightingProbability: 'Moderate',
    description: 'The Mediterranean Parrotfish is a fascinating herbivore with a distinctive beak-like mouth formed by fused teeth. An important species for reef health, it scrapes algae from rocks and contributes to bioerosion. Populations have declined significantly across the Mediterranean due to overfishing.',
    physicalDescription: 'Robust body with a distinctive parrot-like beak formed by fused teeth. Males are grey-green with pink and orange markings. Females are reddish-brown. Can reach up to 50cm in length.',
    behavior: 'Herbivorous, scraping algae from rocks with its beak-like teeth. Produces sand as a byproduct of feeding. Solitary or in small groups. Important for controlling algae growth on reefs. Changes sex from female to male.',
    size: 'Up to 50cm length',
    habitat: 'Rocky reefs, seagrass beds, 0–50 metres depth',
    diverNotes: [
    'Listen for the distinctive scraping sound as it feeds on rocky substrate',
    'Males and females look very different — the female is reddish-brown, the male is green',
    'A sighting is worth reporting to help monitor population recovery',
    'Most commonly encountered at Amphora Reef and Chapel Reef'],

    tags: ['Reef'],
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'Moderate' },
    { name: 'Chapel Reef', probability: 'Moderate' },
    { name: 'Amphora Reef', probability: 'Moderate' },
    { name: 'Cape Greco', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Moderate' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'Moderate' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'Can produce up to 90kg of sand per year through their feeding activity',
    'Sleeps in a mucus cocoon at night for protection from parasites and predators',
    'One of the few herbivorous fish in the Mediterranean',
    'Populations have declined significantly due to overfishing'],

    conservationInfo: 'Listed as Vulnerable on the IUCN Red List. Populations have declined dramatically in the Mediterranean. Protected in some areas. Important for reef health as they control algae growth. Divers should report sightings to help monitor populations.'
  },
  {
    id: 15,
    commonName: 'Red Mullet',
    scientificName: 'Mullus surmuletus',
    category: 'Fish',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ab3d9b7a-1777438019590.png",
    alt: 'Red Mullet (Mullus surmuletus) with reddish coloration and barbels foraging on sandy seabed in Cyprus',
    caption: 'Red Mullet probing the sandy seabed with their sensitive barbels — a common sight on Cyprus sandy bottom dives.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'High',
    description: 'The Red Mullet is a distinctive bottom-dwelling fish with characteristic sensory barbels under its chin. Commonly seen foraging in sandy and mixed-substrate areas, it uses its barbels to probe the seabed for buried invertebrates. A reliable sighting on most Cyprus dive sites.',
    physicalDescription: 'Reddish-pink body with two distinctive barbels under the chin used for sensing prey. Can reach up to 40cm in length. Two separate dorsal fins. Can change colour intensity based on environment.',
    behavior: 'Bottom-dwelling fish that uses barbels to probe sand for small invertebrates. Often seen in small groups. Most active during dawn and dusk. Partially buries itself in sand when resting.',
    size: 'Up to 40cm length',
    habitat: 'Sandy and muddy bottoms, seagrass beds, 0–100 metres',
    diverNotes: [
    'Look for them actively probing sandy substrate with their barbels',
    'Often seen in small groups of 3–10 individuals',
    'Most active during dawn and dusk dives',
    'Commonly encountered at Green Bay, Amphora Reef, and Turtle Bay'],

    tags: ['Sandy Bottom', 'Beginner Friendly'],
    bestDiveSites: [
    { name: 'Green Bay', probability: 'High' },
    { name: 'Zenobia Wreck', probability: 'Moderate' },
    { name: 'Amphora Reef', probability: 'High' },
    { name: 'Turtle Bay', probability: 'High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'Highly prized in Mediterranean cuisine',
    'Their barbels contain taste receptors to detect prey buried in sand',
    'Can change colour from pale pink to deep red',
    'Were considered a delicacy in ancient Rome'],

    conservationInfo: 'Listed as Least Concern. Common throughout Cyprus waters. An important commercially fished species. Populations are monitored and fishing is regulated. Sustainable fishing practices are encouraged.'
  },
  {
    id: 31,
    commonName: 'Lionfish',
    scientificName: 'Pterois miles',
    category: 'Fish',
    image: '/assets/images/lionfish-cyprus-pterois-miles-invasive-1777436793885.jpg',
    alt: 'Lionfish (Pterois miles) invasive species displaying venomous spines in Cyprus Mediterranean waters',
    caption: 'A Lionfish displaying its distinctive venomous spines in Cyprus waters — an invasive species spreading across the Mediterranean.',
    conservationStatus: 'Invasive Species',
    sightingProbability: 'Increasing',
    description: 'The Lionfish is a striking but invasive species now established in Cyprus waters. Originating from the Indo-Pacific, it has rapidly spread throughout the Eastern Mediterranean via the Suez Canal. A highly efficient predator, it feeds on small native fish and disrupts local marine ecosystems. Sightings should be reported to marine authorities.',
    physicalDescription: 'Unmistakable with bold red and white vertical stripes across the body and long, fan-like pectoral fins. Numerous venomous dorsal, pelvic, and anal spines. Typically 25–40cm in length.',
    behavior: 'Often found around reefs, rocky areas, and wrecks. Active hunter, especially at dusk and night. Can hover motionless while stalking prey. Territorial and will stand its ground if approached.',
    size: '25–40cm',
    habitat: 'Reefs, rocky areas, wrecks, 5–40 metres depth',
    diverNotes: [
    'Do not approach or touch — venomous spines cause intense pain and require medical attention',
    'Maintain good buoyancy and a safe distance at all times',
    'Report all sightings to local marine authorities to help track the spread',
    'Most commonly encountered at Cape Greco and Ayia Napa reef systems'],

    tags: ['Reef', 'Wreck', 'Night Dive', 'Invasive Species'],
    bestDiveSites: [
    { name: 'Cape Greco', probability: 'Moderate' },
    { name: 'Zenobia Wreck', probability: 'Moderate' },
    { name: 'Green Bay', probability: 'Low' },
    { name: 'Ayia Napa Reefs', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Moderate' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'Native to the Indo-Pacific, Lionfish entered the Mediterranean through the Suez Canal',
    'A single Lionfish can reduce juvenile fish populations on a reef by up to 79%',
    'Their venomous spines are purely defensive — they do not use them to hunt',
    'Lionfish have no natural predators in the Mediterranean, which accelerates their spread'],

    conservationInfo: 'Classified as an Invasive Alien Species of concern in the Mediterranean. Lionfish pose a significant threat to native fish populations and reef ecosystems. Their rapid spread across Cyprus waters is actively monitored. Divers are encouraged to report all sightings to local marine authorities. Do not approach or touch — venomous spines require medical attention.'
  },
  {
    id: 16,
    commonName: 'Spiny Starfish',
    scientificName: 'Marthasterias glacialis',
    category: 'Invertebrates',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d6524dd2-1777438019990.png",
    alt: 'Spiny Starfish (Marthasterias glacialis) with prominent spines on five arms resting on rocky seabed in Cyprus',
    caption: 'A Spiny Starfish on the rocky seabed — a large and impressive echinoderm found throughout Cyprus dive sites.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'High',
    description: 'The Spiny Starfish is a large and impressive echinoderm, easily recognised by its prominent spines and often vivid colouration. A common sight on Cyprus rocky reefs and wrecks, it is an important predator in the benthic ecosystem.',
    physicalDescription: 'Large starfish with five arms covered in prominent spines. Can reach up to 70cm in diameter. Colour varies from pale cream to bright orange or purple. Arms are thick and robust.',
    behavior: 'Predatory, feeding on molluscs, other echinoderms, and dead animals. Uses tube feet to move slowly across substrate. Can regenerate lost arms. Most active at night but often seen during day dives.',
    size: 'Up to 70cm diameter',
    habitat: 'Rocky reefs, caves, 0–180 metres depth',
    diverNotes: [
    'Do not remove from water or handle roughly — this can cause serious damage',
    'Often found on rocky walls and cave ceilings',
    'Look for them at Zenobia Wreck and Chapel Reef',
    'Colour varies considerably — orange, purple, and cream individuals all occur in Cyprus'],

    tags: ['Reef', 'Wreck', 'Caves', 'Macro'],
    bestDiveSites: [
    { name: 'Zenobia Wreck', probability: 'High' },
    { name: 'Chapel Reef', probability: 'High' },
    { name: 'Cape Greco', probability: 'High' },
    { name: 'Amphora Reef', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'High' },
    { season: 'Winter', probability: 'High' }],

    interestingFacts: [
    'Can regenerate entire arms if lost to predators',
    'Have no brain but can still hunt and navigate',
    'Their stomach can extend outside their body to digest prey',
    'Can live for over 10 years'],

    conservationInfo: 'Listed as Least Concern. Common throughout Cyprus waters. An important predator in the ecosystem. Should not be removed from water or handled roughly as this can damage them.'
  },
  {
    id: 17,
    commonName: 'Purple Sea Urchin',
    scientificName: 'Paracentrotus lividus',
    category: 'Invertebrates',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e427b7df-1777438020642.png",
    alt: 'Purple Sea Urchin (Paracentrotus lividus) with long spines attached to rocky reef surface in Cyprus',
    caption: 'Purple Sea Urchins are a constant presence on Cyprus rocky reefs — important grazers and a hazard for bare skin.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Very High',
    description: 'The Purple Sea Urchin is one of the most abundant invertebrates on Cyprus reefs. Found on virtually every rocky dive site, these spiny grazers play an important role in controlling algae growth. Divers should be aware of their spines, which can cause painful injuries.',
    physicalDescription: 'Spherical body covered in long, sharp spines. Typically purple to dark brown in colour. Can reach up to 7cm in diameter (not including spines). Spines can be up to 3cm long.',
    behavior: 'Herbivorous, grazing on algae. Uses tube feet and spines to move slowly across substrate. Often found in crevices during the day. Can cover themselves with shells and debris for camouflage.',
    size: 'Up to 7cm body diameter, 3cm spines',
    habitat: 'Rocky reefs, seagrass beds, 0–80 metres depth',
    diverNotes: [
    'Avoid contact — spines can penetrate wetsuits and cause painful injuries',
    'Present on virtually every rocky reef dive in Cyprus',
    'Look for them in crevices and on open rocky substrate',
    'An important indicator of reef health — dense populations suggest good algae control'],

    tags: ['Reef', 'Macro', 'Beginner Friendly'],
    bestDiveSites: [
    { name: 'Green Bay', probability: 'Very High' },
    { name: 'Chapel Reef', probability: 'Very High' },
    { name: 'Amphora Reef', probability: 'Very High' },
    { name: 'Zenobia Wreck', probability: 'High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'Very High' }],

    interestingFacts: [
    'They have five teeth arranged in a structure called Aristotle\'s lantern',
    'Can live for over 30 years',
    'Their roe is considered a delicacy in Mediterranean cuisine',
    'Play a crucial role in preventing algae overgrowth on reefs'],

    conservationInfo: 'Listed as Least Concern but populations are monitored. Overharvesting for roe has caused declines in some areas. Fishing is regulated with size limits and seasonal closures. Important for reef ecosystem health. Divers should avoid touching as spines can cause painful injuries.'
  },
  {
    id: 18,
    commonName: 'Mediterranean Slipper Lobster',
    scientificName: 'Scyllarides latus',
    category: 'Invertebrates',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c2a776d8-1777438019351.png",
    alt: 'Mediterranean Slipper Lobster (Scyllarides latus) with flattened body hiding in rocky crevice in Cyprus',
    caption: 'The Mediterranean Slipper Lobster is a rare and impressive crustacean — a special sighting on Cyprus night dives.',
    conservationStatus: 'Data Deficient',
    sightingProbability: 'Rare',
    description: 'The Mediterranean Slipper Lobster is a large, armoured crustacean and a rare sighting for divers in Cyprus. Distinguished by its broad, shovel-like antennae rather than claws, it hides in caves and rocky crevices during the day and emerges at night to feed.',
    physicalDescription: 'Large, flattened lobster with broad, shovel-like antennae instead of claws. Brown to reddish colour with white spots. Can reach up to 45cm in length and weigh over 2kg. Armoured body with a rough texture.',
    behavior: 'Nocturnal, hiding in caves and crevices during the day. Feeds on molluscs and echinoderms. Slow-moving but can swim backwards rapidly when threatened. Solitary except during breeding season.',
    size: 'Up to 45cm length, 2kg+',
    habitat: 'Rocky reefs, caves, 0–100 metres depth',
    diverNotes: [
    'Best chance of sighting on night dives at cave-rich sites',
    'Look for them in cave entrances and deep rocky crevices',
    'Do not disturb or attempt to handle — a protected species',
    'Any sighting should be reported to help monitor populations'],

    tags: ['Caves', 'Night Dive'],
    bestDiveSites: [
    { name: 'Cape Greco', probability: 'Low' },
    { name: 'Chapel Reef', probability: 'Low' },
    { name: 'Zenobia Wreck', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Low' },
    { season: 'Summer', probability: 'Moderate' },
    { season: 'Autumn', probability: 'Low' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'One of the largest crustaceans in the Mediterranean',
    'Can live for over 20 years',
    'Highly prized in cuisine, leading to significant overfishing',
    'Their larvae drift in ocean currents for up to 6 months before settling'],

    conservationInfo: 'Listed as Data Deficient on the IUCN Red List but believed to be declining. Protected in many Mediterranean areas. Fishing is strictly regulated or banned in Cyprus waters. Any sightings should be reported to help monitor populations. Divers should never disturb or attempt to catch them.'
  },
  {
    id: 19,
    commonName: 'Mediterranean Spider Crab',
    scientificName: 'Maja squinado',
    category: 'Invertebrates',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_177c55657-1777438019961.png",
    alt: 'Mediterranean Spider Crab (Maja squinado) with long legs and algae-covered shell on rocky seabed in Cyprus',
    caption: 'A Mediterranean Spider Crab camouflaged with algae and sponges — easy to miss even when in plain sight.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Moderate',
    description: 'The Mediterranean Spider Crab is a fascinating crustacean that actively decorates its shell with algae, sponges, and other organisms for camouflage. Despite its large size, it can be surprisingly difficult to spot. A rewarding find for observant divers on Cyprus rocky reefs.',
    physicalDescription: 'Large crab with long, spindly legs and a triangular carapace covered in spines. Reddish-brown colour, often covered in algae and small organisms. Can reach up to 20cm carapace width with a leg span up to 50cm.',
    behavior: 'Slow-moving, relies on camouflage for protection. Actively decorates its shell with algae, sponges, and other organisms. Feeds on algae, small invertebrates, and dead animals. Often found in rocky areas and seagrass beds.',
    size: '20cm carapace, 50cm leg span',
    habitat: 'Rocky reefs, seagrass beds, 0–100 metres depth',
    diverNotes: [
    'Look carefully at algae-covered rocks — spider crabs are masters of camouflage',
    'Most commonly encountered in spring when aggregations form for moulting',
    'Do not disturb or remove from habitat',
    'Found at Green Bay, Amphora Reef, and Chapel Reef'],

    tags: ['Reef', 'Sandy Bottom', 'Macro'],
    bestDiveSites: [
    { name: 'Green Bay', probability: 'Moderate' },
    { name: 'Amphora Reef', probability: 'Moderate' },
    { name: 'Chapel Reef', probability: 'Moderate' },
    { name: 'Zenobia Wreck', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'Moderate' },
    { season: 'Autumn', probability: 'Moderate' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'They actively decorate themselves with algae and sponges for camouflage',
    'Form large aggregations during breeding season',
    'Can live for over 8 years',
    'Their decorated appearance makes them difficult to spot even when in plain sight'],

    conservationInfo: 'Listed as Least Concern. Populations are stable in Cyprus waters. Commercially fished in some areas but not heavily targeted. Should not be disturbed or removed from their habitat.'
  },
  {
    id: 20,
    commonName: 'Common Cuttlefish',
    scientificName: 'Sepia officinalis',
    category: 'Invertebrates',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1579046fb-1770368585499.png',
    alt: 'Common Cuttlefish (Sepia officinalis) displaying colour-changing abilities with distinctive W-shaped pupils near sandy seabed in Cyprus',
    caption: 'A Common Cuttlefish hovering above the seabed — one of the most intelligent and visually captivating species in Cyprus waters.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Moderate',
    description: 'The Common Cuttlefish is an intelligent cephalopod with remarkable colour-changing abilities. Frequently encountered in Cyprus waters, it is one of the most behaviourally complex invertebrates a diver can observe. Males display elaborate colour patterns during courtship, making spring encounters particularly rewarding.',
    physicalDescription: 'Oval body with eight arms and two longer tentacles. Can reach up to 45cm in length. Has a distinctive W-shaped pupil. Internal cuttlebone provides buoyancy. Can change colour and texture instantly.',
    behavior: 'Highly intelligent with complex behaviours. Masters of camouflage, can change colour and texture in milliseconds. Hunts small fish and crustaceans using tentacles. Uses jet propulsion for rapid movement. Males display elaborate courtship colour patterns.',
    size: 'Up to 45cm length',
    habitat: 'Sandy and muddy bottoms, seagrass beds, 0–200 metres',
    diverNotes: [
    'Approach slowly and avoid sudden movements to observe natural behaviour',
    'Spring is the best time to witness elaborate male courtship displays',
    'Look for them hovering above sandy seabeds and seagrass areas',
    'Do not touch — they can be stressed by handling and may ink defensively'],

    tags: ['Sandy Bottom', 'Macro', 'Night Dive'],
    bestDiveSites: [
    { name: 'Green Bay', probability: 'Moderate' },
    { name: 'Turtle Bay', probability: 'Moderate' },
    { name: 'Amphora Reef', probability: 'Moderate' },
    { name: 'Zenobia Wreck', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'Moderate' },
    { season: 'Autumn', probability: 'Low' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'Can change colour in less than a second using specialised skin cells',
    'Have three hearts and blue-green blood',
    'Their eyes can perceive polarised light',
    'Males engage in elaborate colour displays during courtship'],

    conservationInfo: 'Listed as Least Concern. Populations fluctuate naturally. Commercially fished but populations appear stable. An important predator in the ecosystem. Divers should observe without touching as they can be stressed by interaction.'
  },
  {
    id: 21,
    commonName: 'Nudibranch',
    scientificName: 'Flabellina affinis',
    category: 'Invertebrates',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6490dbe-1777438019274.png",
    alt: 'Flabellina nudibranch (Flabellina affinis) with purple body and orange cerata on rocky Mediterranean reef in Cyprus',
    caption: 'A Flabellina nudibranch on a Cyprus reef — small, vivid, and a favourite subject for macro photographers.',
    conservationStatus: 'Not Evaluated',
    sightingProbability: 'Moderate',
    description: 'Nudibranchs are colourful sea slugs and a favourite subject for macro photographers in Cyprus. Flabellina affinis is one of the most commonly encountered species, with its vivid purple body and orange cerata making it unmistakable. Good buoyancy control is essential for close observation.',
    physicalDescription: 'Small, colourful sea slug with a purple body and orange and white cerata (finger-like projections). Typically 3–4cm in length. No shell. Vivid colours warn predators of toxicity.',
    behavior: 'Slow-moving, feeds on hydroids and other small invertebrates. Hermaphroditic — all individuals have both male and female reproductive organs. Lays distinctive spiral egg masses. Absorbs toxins from prey for defence.',
    size: '3–4cm length',
    habitat: 'Rocky reefs, under overhangs, 0–40 metres depth',
    diverNotes: [
    'Excellent buoyancy control is essential — avoid stirring up sediment near them',
    'Look carefully on rocky walls and under overhangs',
    'Best encountered at Chapel Reef and Cape Greco cave systems',
    'Never touch — they are extremely delicate and easily damaged'],

    tags: ['Reef', 'Caves', 'Macro', 'Night Dive'],
    bestDiveSites: [
    { name: 'Chapel Reef', probability: 'Moderate' },
    { name: 'Cape Greco', probability: 'Moderate' },
    { name: 'Amphora Reef', probability: 'Moderate' },
    { name: 'Green Bay', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'Moderate' },
    { season: 'Autumn', probability: 'Moderate' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'There are over 3,000 species of nudibranchs worldwide',
    'They can incorporate stinging cells from prey into their own tissue for defence',
    'Each species has unique colour patterns',
    'Popular subjects for underwater macro photography'],

    conservationInfo: 'Not formally evaluated but not considered threatened. Sensitive to water quality and pollution. Their presence indicates a healthy reef ecosystem. Should never be touched as they are delicate and easily damaged. Divers should practise good buoyancy control when observing them.'
  },
  {
    id: 22,
    commonName: 'Mauve Stinger Jellyfish',
    scientificName: 'Pelagia noctiluca',
    category: 'Invertebrates',
    image: "https://images.unsplash.com/photo-1707053010178-080ba9fed085",
    alt: 'Mauve Stinger jellyfish (Pelagia noctiluca) with translucent pink bell and trailing tentacles in Cyprus Mediterranean water',
    caption: 'The Mauve Stinger is a common jellyfish in Cyprus waters — beautiful but capable of a painful sting.',
    conservationStatus: 'Not Evaluated',
    sightingProbability: 'Moderate',
    description: 'The Mauve Stinger is a common jellyfish in Cyprus waters, particularly during summer months. While visually striking with its translucent pink bell and bioluminescent properties, it delivers a painful sting and divers should maintain distance. Blooms can be large and widespread.',
    physicalDescription: 'Small jellyfish with a translucent pink to purple bell, typically 6–12cm in diameter. Has eight long tentacles and four oral arms. Bioluminescent — glows when disturbed. Distinctive warty appearance on bell.',
    behavior: 'Pelagic, drifting with currents. Feeds on small fish and zooplankton using stinging tentacles. Most common in summer months. Forms large blooms that can number in the millions.',
    size: '6–12cm bell diameter',
    habitat: 'Open water, surface to 150 metres depth',
    diverNotes: [
    'Maintain distance from tentacles — the sting is painful and can cause allergic reactions',
    'Wear a full wetsuit during bloom periods for protection',
    'Most common in summer — check local conditions before diving',
    'Stings should be treated with vinegar or hot water, not fresh water'],

    tags: ['Night Dive'],
    bestDiveSites: [
    { name: 'All open water sites', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Low' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'Moderate' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'Bioluminescent — produces light when disturbed at night',
    'Can form massive blooms with millions of individuals',
    'Their sting is painful and can cause allergic reactions in sensitive individuals',
    'Populations appear to be increasing in the Mediterranean, possibly linked to climate change'],

    conservationInfo: 'Not formally evaluated. Populations fluctuate naturally but appear to be increasing, possibly due to climate change and overfishing of predators. An important part of the food web. Divers should maintain distance and wear protective clothing during bloom periods.'
  },
  {
    id: 32,
    commonName: 'Common Octopus',
    scientificName: 'Octopus vulgaris',
    category: 'Invertebrates',
    image: '/assets/images/octopus-cyprus-diving-mediterranean-1777437432495.jpg',
    alt: 'Common Octopus (Octopus vulgaris) on sandy seabed in Cyprus Mediterranean waters showing natural camouflage',
    caption: 'A Common Octopus moving across the seabed in Cyprus waters, showcasing its natural camouflage and fluid movement.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'High',
    description: 'The Common Octopus is one of the most intelligent and fascinating marine species found in Cyprus waters. Known for its remarkable ability to change colour and texture, it blends seamlessly into rocks and sand for both hunting and defence. Divers often encounter octopuses hiding in crevices or moving gracefully across the seabed, especially during dusk or night dives.',
    physicalDescription: 'Soft-bodied cephalopod with eight arms lined with suckers. Highly variable in colour and texture due to advanced chromatophore cells. Adults typically reach 30–60cm arm span. The mantle is bulbous and muscular, housing the main organs.',
    behavior: 'Solitary and territorial. Uses jet propulsion for rapid escape and crawls along the seabed when foraging. Highly intelligent — capable of problem-solving and tool use. More active during dusk and night. Hides in dens among rocks and crevices during the day.',
    size: '30–60cm arm span',
    habitat: 'Rocky reefs, sandy seabeds, crevices, 0–200 metres depth',
    diverNotes: [
    'Highly intelligent and curious — approach slowly and they may investigate you',
    'Never touch or disturb — handling causes stress and can harm them',
    'More active during dusk and night dives',
    'Look for shell and debris piles near crevices — a sign of an octopus den'],

    tags: ['Reef', 'Sandy Bottom', 'Night Dive', 'Macro', 'Beginner Friendly'],
    bestDiveSites: [
    { name: 'Cape Greco', probability: 'High' },
    { name: 'Protaras & Ayia Napa Reefs', probability: 'High' },
    { name: 'Paphos Rocky Seabeds', probability: 'High' },
    { name: 'Zenobia Wreck', probability: 'Moderate' },
    { name: 'Limassol Dive Sites', probability: 'Moderate' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'High' },
    { season: 'Summer', probability: 'High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'Moderate' }],

    interestingFacts: [
    'Octopuses have three hearts and blue blood due to copper-based haemocyanin',
    'They can change colour and texture in milliseconds using specialised skin cells',
    'Each arm has its own neural network, allowing semi-independent movement',
    'The Common Octopus is considered one of the most intelligent invertebrates on Earth'],

    conservationInfo: 'Listed as Least Concern on the IUCN Red List. Common in Mediterranean waters including Cyprus. Highly intelligent and curious — divers should observe from a respectful distance and never touch or disturb them. Their presence is a positive indicator of healthy reef ecosystems.'
  },
  {
    id: 23,
    commonName: 'Bottlenose Dolphin',
    scientificName: 'Tursiops truncatus',
    category: 'Marine Mammals',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18188bf00-1777438019233.png",
    alt: 'Bottlenose Dolphin (Tursiops truncatus) swimming in clear blue Mediterranean waters near Cyprus with distinctive curved dorsal fin',
    caption: 'A Bottlenose Dolphin in Cyprus waters — an intelligent and charismatic marine mammal and a rare but memorable encounter.',
    conservationStatus: 'Vulnerable',
    sightingProbability: 'Rare',
    description: 'The Bottlenose Dolphin is an intelligent and highly social marine mammal occasionally encountered in Cyprus coastal waters. While sightings during dives are rare, they are sometimes seen from boats or in open water. Any underwater encounter is considered exceptional.',
    physicalDescription: 'Large dolphin with a robust body and distinctive short beak. Grey colouration, lighter on the belly. Can reach 2.5–4 metres in length and weigh 150–650kg. Curved dorsal fin. Permanent "smile" due to jaw structure.',
    behavior: 'Highly social, living in pods of 10–30 individuals. Intelligent with complex communication and social behaviours. Feeds on fish and squid. Can dive to depths over 500 metres. Playful and curious.',
    size: '2.5–4 metres length, 150–650kg',
    habitat: 'Coastal and offshore waters, 0–500+ metres',
    diverNotes: [
    'Never chase or attempt to touch — observe calmly and let them approach if they choose',
    'Maintain respectful distance and avoid sudden movements',
    'Most likely encountered in open water rather than at specific dive sites',
    'Any sighting should be reported to help monitor local populations'],

    tags: ['Beginner Friendly'],
    bestDiveSites: [
    { name: 'Open water encounters', probability: 'Very Low' },
    { name: 'Akrotiri Peninsula', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Low' },
    { season: 'Summer', probability: 'Low' },
    { season: 'Autumn', probability: 'Low' },
    { season: 'Winter', probability: 'Very Low' }],

    interestingFacts: [
    'Can recognise themselves in mirrors, demonstrating self-awareness',
    'Use echolocation to navigate and hunt',
    'Can hold their breath for over 8 minutes',
    'Have been observed using tools (sponges) to protect their beaks while foraging'],

    conservationInfo: 'Listed as Vulnerable in the Mediterranean. Threats include pollution, boat strikes, fishing gear entanglement, and noise pollution. Protected under EU and international law. Any sightings should be reported. Divers should never chase or attempt to touch dolphins.'
  },
  {
    id: 24,
    commonName: 'Mediterranean Monk Seal',
    scientificName: 'Monachus monachus',
    category: 'Marine Mammals',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12c34e06c-1772357911167.png",
    alt: 'Mediterranean Monk Seal (Monachus monachus) swimming underwater in coastal Mediterranean waters near Cyprus',
    caption: 'The Mediterranean Monk Seal is one of the world\'s rarest marine mammals — any sighting in Cyprus is exceptional.',
    conservationStatus: 'Endangered',
    sightingProbability: 'Extremely Rare',
    description: 'The Mediterranean Monk Seal is one of the world\'s rarest marine mammals, with fewer than 700 individuals remaining. Occasional sightings have been recorded in Cyprus waters, suggesting the area may form part of their range. Any encounter is an extraordinary event and must be handled with extreme care.',
    physicalDescription: 'Large seal with dark brown to grey fur, lighter on the belly. Males can reach 2.4 metres and weigh up to 320kg. Females are smaller. Short, broad flippers. No external ear flaps.',
    behavior: 'Shy and elusive, avoiding human contact. Feeds on fish, octopus, and squid. Rests in sea caves with underwater entrances. Solitary or in small groups. Can dive to depths over 250 metres.',
    size: 'Up to 2.4 metres, 320kg',
    habitat: 'Coastal caves, rocky shores, 0–250 metres',
    diverNotes: [
    'If encountered, leave the area immediately and quietly — do not approach',
    'Never block the entrance to a sea cave that may be used as a resting site',
    'Any sighting must be reported immediately to marine authorities',
    'Disturbance can cause mothers to abandon pups — treat any encounter with maximum caution'],

    tags: ['Caves'],
    bestDiveSites: [
    { name: 'Remote coastal caves', probability: 'Extremely Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Extremely Low' },
    { season: 'Summer', probability: 'Extremely Low' },
    { season: 'Autumn', probability: 'Extremely Low' },
    { season: 'Winter', probability: 'Extremely Low' }],

    interestingFacts: [
    'One of the world\'s rarest marine mammals with fewer than 700 individuals remaining',
    'Can live for over 40 years',
    'Pups are born with black fur that gradually lightens with age',
    'Cyprus has recorded occasional sightings, suggesting the area may be part of their range'],

    conservationInfo: 'Listed as Endangered on the IUCN Red List. Critically endangered in the Mediterranean with fewer than 700 individuals remaining. Protected under EU and international law. Main threats include habitat disturbance, fishing gear entanglement, and human disturbance. ANY sighting should be immediately reported to authorities. Divers must never approach, disturb, or attempt to interact with monk seals.'
  },
  {
    id: 25,
    commonName: 'Common Stingray',
    scientificName: 'Dasyatis pastinaca',
    category: 'Rays & Sharks',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fcd74226-1777438019218.png",
    alt: 'Common Stingray (Dasyatis pastinaca) with diamond-shaped body resting on sandy seabed in Cyprus Mediterranean waters',
    caption: 'A Common Stingray resting on the sandy seabed — a graceful bottom-dweller occasionally encountered in Cyprus.',
    conservationStatus: 'Data Deficient',
    sightingProbability: 'Moderate',
    description: 'The Common Stingray is a graceful bottom-dweller occasionally encountered in Cyprus waters. Often partially buried in sand with only its eyes visible, it is generally docile and poses no threat to divers who maintain awareness and avoid contact. A calm and rewarding encounter when observed from a respectful distance.',
    physicalDescription: 'Diamond-shaped body with a long whip-like tail armed with a venomous spine. Brown to grey colouration, lighter underneath. Can reach up to 1.4 metres in width. Smooth skin without thorns on the back.',
    behavior: 'Bottom-dwelling, often buried in sand with only eyes visible. Feeds on molluscs, crustaceans, and small fish. Uses electroreceptors to detect prey. Generally docile but will use its tail spine in self-defence if stepped on or threatened.',
    size: 'Up to 1.4 metres width, 2.5 metres total length',
    habitat: 'Sandy and muddy bottoms, seagrass beds, 0–200 metres',
    diverNotes: [
    'Shuffle feet when walking in shallow sandy areas to avoid stepping on buried rays',
    'Observe from a distance — do not corner or approach from behind',
    'The tail spine is used only in self-defence — a calm diver poses no risk',
    'Look for the outline of a buried ray on sandy seabeds'],

    tags: ['Sandy Bottom'],
    bestDiveSites: [
    { name: 'Green Bay', probability: 'Low' },
    { name: 'Turtle Bay', probability: 'Moderate' },
    { name: 'Amphora Reef', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Moderate' },
    { season: 'Summer', probability: 'Moderate' },
    { season: 'Autumn', probability: 'Moderate' },
    { season: 'Winter', probability: 'Low' }],

    interestingFacts: [
    'Their tail spine is serrated and covered in venomous mucus',
    'Can detect electrical fields produced by prey buried in sand',
    'Give birth to live young (ovoviviparous)',
    'Often bury themselves completely in sand for camouflage'],

    conservationInfo: 'Listed as Data Deficient on the IUCN Red List. Populations are not well studied. Caught as bycatch in fisheries. An important predator in sandy bottom ecosystems. Divers should watch where they step and shuffle feet when walking in shallow sandy areas. Stings are painful but rarely life-threatening.'
  },
  {
    id: 26,
    commonName: 'Common Eagle Ray',
    scientificName: 'Myliobatis aquila',
    category: 'Rays & Sharks',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11d56fa54-1777438019587.png",
    alt: 'Common Eagle Ray (Myliobatis aquila) with dark body and white underside gliding through open Mediterranean water near Cyprus',
    caption: 'A Common Eagle Ray gliding through open water — one of the most spectacular encounters possible in Cyprus.',
    conservationStatus: 'Vulnerable',
    sightingProbability: 'Rare',
    description: 'The Common Eagle Ray is a spectacular species, gracefully gliding through Cyprus waters like an underwater bird in flight. Unlike most rays, it is an active, pelagic swimmer rather than a bottom-dweller. A rare but unforgettable encounter for divers in Cyprus.',
    physicalDescription: 'Distinctive ray with a pointed snout and wing-like pectoral fins. Dark brown to black on top with a white underside. Can reach up to 1.8 metres in width. Long whip-like tail with a venomous spine.',
    behavior: 'Pelagic and active swimmer, unlike most rays. Often seen gliding through open water. Feeds on molluscs and crustaceans, crushing shells with plate-like teeth. Sometimes leaps out of the water. Usually solitary but occasionally in small groups.',
    size: 'Up to 1.8 metres width, 2.5 metres total length',
    habitat: 'Open water, sandy bottoms, 0–200 metres depth',
    diverNotes: [
    'Observe from a distance — do not chase or attempt to intercept',
    'Most likely encountered in open water at sites like Zenobia Wreck',
    'A rare sighting — remain still and let the ray pass naturally',
    'Any sighting should be reported to help monitor local populations'],

    tags: ['Sandy Bottom'],
    bestDiveSites: [
    { name: 'Open water encounters', probability: 'Very Low' },
    { name: 'Zenobia Wreck', probability: 'Low' },
    { name: 'Akrotiri Peninsula', probability: 'Low' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Low' },
    { season: 'Summer', probability: 'Moderate' },
    { season: 'Autumn', probability: 'Low' },
    { season: 'Winter', probability: 'Very Low' }],

    interestingFacts: [
    'Can leap several metres out of the water, possibly to remove parasites',
    'Their teeth are fused into crushing plates for eating hard-shelled prey',
    'Excellent swimmers, more active than most ray species',
    'A rare but spectacular sighting for Mediterranean divers'],

    conservationInfo: 'Listed as Vulnerable on the IUCN Red List. Populations have declined due to overfishing and bycatch. Protected in some Mediterranean areas. An important predator in the ecosystem. Any sightings should be reported. Divers should observe from a distance and not chase or corner them.'
  },
  {
    id: 30,
    commonName: 'Saddled Seabream',
    scientificName: 'Oblada melanura',
    category: 'Fish',
    image: '/assets/images/cyprus-saddled-seabream-underwater-1777361247864.jpg',
    alt: 'Saddled Seabream (Oblada melanura) with distinctive black tail spot swimming over rocky reef in Cyprus waters',
    caption: 'Easily recognised by the black spot near its tail, the Saddled Seabream is a constant presence on Cyprus reefs.',
    conservationStatus: 'Least Concern',
    sightingProbability: 'Very High',
    description: 'The Saddled Seabream is one of the most commonly encountered reef fish in Cyprus. Easily recognised by the distinctive black spot at the base of its tail, it is typically seen in small groups around rocky slopes, wrecks, and seagrass beds. A reliable and approachable species on virtually every Cyprus dive.',
    physicalDescription: 'Silvery-grey body with a distinctive black spot at the base of the tail. Can reach up to 30cm in length. Oval-shaped body with a single dorsal fin. The black saddle marking near the tail is the key identifier.',
    behavior: 'Forms schools of varying sizes around rocky slopes and wrecks. Omnivorous, feeding on algae, small invertebrates, and zooplankton. Active during the day and a reliable, everyday sighting for divers in Cyprus.',
    size: 'Up to 30cm length',
    habitat: 'Rocky reefs, seagrass beds, coastal waters, 0–30 metres',
    diverNotes: [
    'One of the most reliably encountered fish on any Cyprus dive',
    'Often seen in mixed schools with other reef fish',
    'Approachable and calm — a good species for beginner divers',
    'The black tail spot is the key identification feature'],

    tags: ['Reef', 'Beginner Friendly'],
    bestDiveSites: [
    { name: 'Green Bay', probability: 'Very High' },
    { name: 'Chapel Reef', probability: 'Very High' },
    { name: 'Zenobia Wreck', probability: 'High' },
    { name: 'Amphora Reef', probability: 'Very High' }],

    seasonalAvailability: [
    { season: 'Spring', probability: 'Very High' },
    { season: 'Summer', probability: 'Very High' },
    { season: 'Autumn', probability: 'Very High' },
    { season: 'Winter', probability: 'High' }],

    interestingFacts: [
    'The black spot near the tail is thought to confuse predators about which end is the head',
    'They are hermaphroditic, with some individuals changing sex during their lifetime',
    'Very adaptable and can thrive in various habitats',
    'A popular target for recreational fishing in Cyprus'],

    conservationInfo: 'Listed as Least Concern. Very abundant throughout Cyprus waters. An important part of the reef ecosystem. Recreationally fished but populations remain healthy. No special conservation measures required.'
  }];


  const filteredSpecies = speciesData.filter((species) => {
    const matchesCategory = activeCategory === 'all' || species.category.toLowerCase().replace(/\s+/g, '-') === activeCategory;
    const matchesSearch = species.commonName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    species.scientificName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    species.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleSpeciesClick = (species: Species) => {
    setSelectedSpecies(species);
  };

  const handleCloseModal = () => {
    setSelectedSpecies(null);
  };

  if (!isHydrated) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Icon name="ArrowPathIcon" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading marine life data...</p>
        </div>
      </div>);

  }

  return (
    <div className="space-y-8">
      <ConservationBanner />

      <div className="space-y-6">
        <SearchBar onSearch={handleSearch} />
        <CategoryFilter
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange} />

      </div>

      <div className="flex items-center justify-between">
        <p className="text-muted-foreground">
          Showing <span className="font-semibold text-foreground">{filteredSpecies.length}</span> species
        </p>
      </div>

      {filteredSpecies.length === 0 ?
      <div className="text-center py-16">
          <Icon name="MagnifyingGlassIcon" size={64} className="text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No species found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filter criteria</p>
        </div> :

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSpecies.map((species) =>
        <SpeciesCard
          key={species.id}
          species={species}
          onClick={() => handleSpeciesClick(species)} />

        )}
        </div>
      }

      {selectedSpecies &&
      <SpeciesModal species={selectedSpecies} onClose={handleCloseModal} />
      }
    </div>);

};

export default MarineLifeInteractive;